/**
 * Laufzeit-Umgebungswerte (Runtime-Config).
 *
 * Die umgebungsspezifischen OIDC-Werte (Authentik, issue #6) kommen zur
 * LAUFZEIT aus `window.__ENV__`. Diese Variable wird vom nginx-Container beim
 * Start aus seinen Environment-Variablen erzeugt (siehe
 * frontend/runtime/entrypoint.sh + config.js.template; eingebunden via
 * <script src="/config.js"> in index.html). Fallback ist das Build-Env
 * (import.meta.env.VITE_*), damit `npm run dev` lokal weiter mit .env.local
 * funktioniert.
 *
 * Dadurch ist EIN gebautes Frontend-Image fuer Staging und Produktion
 * identisch (Image-Promotion).
 */

interface RuntimeEnv {
  OIDC_ISSUER_URL?: string
  OIDC_CLIENT_ID?: string
  /**
   * Zusätzliche Scopes je Umgebung. `openid profile email offline_access`
   * kommen ohnehin dazu (siehe `lib/oidcScope.ts`) — hier steht nur, was
   * darüber hinaus nötig ist, etwa `groups`.
   */
  OIDC_SCOPE?: string
  TESTER_URL?: string
  PROZESSE_URL?: string
  /** Ziel des App-Switchers: der CAD-Viewer derselben Instanz. */
  CAD_URL?: string
}

const runtime: RuntimeEnv =
  (typeof window !== "undefined" && (window as unknown as { __ENV__?: RuntimeEnv }).__ENV__) || {}

// Ein nicht ersetzter envsubst-Platzhalter ("__OIDC_CLIENT_ID__") oder ein
// leerer String gilt als "nicht gesetzt" -> dann greift der Build-Fallback (lokal).
function resolve(runtimeValue: string | undefined, buildValue: string | undefined): string {
  const candidate = (runtimeValue ?? "").trim()
  const isPlaceholder =
    candidate === "" || (candidate.startsWith("__") && candidate.endsWith("__"))
  return isPlaceholder ? buildValue || "" : candidate
}

/**
 * OIDC-Issuer exakt wie im iss-Claim, z. B.
 * https://login.my-lehner.app/application/o/lehner-erp-stag/
 */
export const OIDC_ISSUER_URL = resolve(
  runtime.OIDC_ISSUER_URL,
  import.meta.env.VITE_OIDC_ISSUER_URL,
)
export const OIDC_CLIENT_ID = resolve(
  runtime.OIDC_CLIENT_ID,
  import.meta.env.VITE_OIDC_CLIENT_ID,
)

/**
 * True, wenn OIDC konfiguriert ist (Issuer + Client-ID vorhanden). Steuert,
 * ob Auth-Routen/ProtectedRoute aktiv sind — im Scaffold ohne IdP bleibt die
 * App lauffähig (LEH-001-Verhalten, unverändert).
 */
export const OIDC_CONFIGURED = Boolean(OIDC_ISSUER_URL && OIDC_CLIENT_ID)

/**
 * Schwester-Werkzeuge der Instanz (Kopfzeilen-Links, LEH-108).
 * Leer = Link wird nicht angezeigt. Lehner: tester./prozesse.my-lehner.app
 */
export const TESTER_URL = resolve(runtime.TESTER_URL, import.meta.env.VITE_TESTER_URL)
export const PROZESSE_URL = resolve(runtime.PROZESSE_URL, import.meta.env.VITE_PROZESSE_URL)

/**
 * CAD-Viewer derselben Lehner-Instanz — Ziel des App-Switchers in der
 * Kopfzeile (Nutzerwunsch 03.08.2026). Leer = kein Umschalter.
 */
export const CAD_URL = resolve(runtime.CAD_URL, import.meta.env.VITE_CAD_URL)

export const OIDC_SCOPE = runtime.OIDC_SCOPE || import.meta.env.VITE_OIDC_SCOPE || ""
